import React, { Component, Fragment } from "react";
import Helmet from "react-helmet";
import { Route, Switch, Redirect } from "react-router-dom";
import MainContainer from "./main-container";
import DrawerContainer from "./drawer";
import OuttakesDrawerContainer from "./outtakes-drawer";
import FriendsAndFamilyDrawerContainer from "./friends-and-family-drawer";
import InfoCardContainer from "./info-card";
import TimelineYearsContainer from "./timeline-years";
import TimelineBasicContainer from "./timeline-basic";
import TimelineMonthsContainer from "./timeline-months";
import PhotosContainer from "./photos";
import DocumentsContainer from "./documents";
import PressContainer from "./press";
import MemorabiliaContainer from "./memorabilia";
import DrawerFront from "./drawer-front";
import OuttakesDrawerFront from "./outtakes-drawer-front";
import FriendsAndFamilyDrawerFront from "./friends-and-family-drawer-front";
import PlaylistsDrawerContainer from "./playlists-drawer";
import PlaylistsDrawerFront from "./playlists-drawer-front";
import AlbumModal from "./album-modal";
import PPVModal from "./news/ppv/ppv-modal";
import ModalWrapper from "./performance-log/modal-wrapper";
import ItineraryModal from "./performance-log/itinerary-notes";
import Map from "./map";
import GlobalMenu from "./global-menu";
import VideoPlayerContainer from "./video-player";
import LiveStreamContainer from "./live";
import LiveStreamTeaserContainer from "./live-teaser";
import GettingStartedContainer from "./getting-started";
import SearchContainer from "./search/search";
import SearchRecordStoreDayContainer from "./search-record-store-day";
import NewsArticle from "./news/article";
import PPV_Page from "./news/ppv/ppv";
import NewsletterContainer from "./newsletter";
import ContactContainer from "./contact";
import AccountInfo from "./account-info/index";
import MovieNightHome from "./movie-night/home/home";
import MovieNight from "./movie-night/movie-night";
import Page404 from "./404.js";
import TestContainer from "./test";
import { Provider, connect } from "react-redux";
import { showLiveStream, showLiveStreamTeaser } from "./toggleLiveStreams";
import HOCRequireAuth from "./hoc-require-auth";
import RequestModal from "./performance-log/requestModal";
import Player from "./components/new-player/new-player";
import TcEmbed from "./newspaper-iframe/tc-embed";
import ContentfulPageContainer from "./contentful-pages/page";
import { withRouter } from "react-router-dom"
import FreeTrialRust from "./account-info/free-trial-rust.js";
import AboutRust from "./account-info/about-rust.js";
import GreenlandPromo from "./account-info/greenland.js";
import CabinetSwitcherPage from "./components/CabinetSwitcherPage";
import { isMobile, isIPad, isIPhone, isPhoneDevice } from "./platform";
import {
  ResponsiveAbout,
  ResponsiveCredits,
  ResponsiveTerms,
  ResponsivePrivacy,
  ResponsiveLogin,
  ResponsiveLogout,
  ResponsiveMap,
  ResponsiveContact,
  ResponsiveWelcome,
  ResponsiveShell,
  UniversalLink,
} from "./responsive/index";

const isMobileView = isPhoneDevice;

/** Helper: wrap a component inside the responsive shell (navbar + sidebar) */
const withShell = (Comp) => (props) => (
  <ResponsiveShell><Comp {...props} /></ResponsiveShell>
);

// Stable module-level references — must NOT be created inside render().
// Inline withShell(Comp) in render creates a new function ref on every render,
// React Router sees a changed 'component' prop and unmounts/remounts the entire
// tree (sidebar state reset, API refetch, ~2s delay on same-route navigation).
const ShellNewspaperPage = isMobileView ? withShell(TcEmbed) : TcEmbed;
const renderWithShell = (node) => () => (
  <ResponsiveShell>{node}</ResponsiveShell>
);

const mapContentfulPages = (pages) => {
  return pages.map((page) => {
    return (
      <Route
        key={page._id}
        path={page.routePath}
        render={(props) => <ContentfulPageContainer page={page} {...props} />}
      />
    );
  });
};

const MainRoutes = ({ pages, location }) => {
  const background = location.state && location.state.modal ? true : false;
  const backgroundLocation = background ? location.state.prevLocation : null;

  return (
    <MainContainer>
      <Switch location={background ? backgroundLocation : location}>
        <Route exact path="/welcome" component={isMobileView ? withShell(ResponsiveWelcome) : DrawerFront} />
        <Route exact path="/friends-and-family-home" component={FriendsAndFamilyDrawerFront} />
        <Route exact path="/outtakes-home" component={OuttakesDrawerFront} />
        <Route exact path="/playlists-home" component={PlaylistsDrawerFront} />
        <Route exact path="/cabinet-switcher" component={CabinetSwitcherPage} />
        <Route
          path="/video"
          render={(props) => (
            <HOCRequireAuth {...props}>
              <VideoPlayerContainer {...props} />
            </HOCRequireAuth>
          )}
        />
        {showLiveStream && (
          <Route path="/live" component={HOCRequireAuth(LiveStreamContainer)} />
        )}
        {showLiveStreamTeaser && (
          <Route
            path="/live"
            component={HOCRequireAuth(LiveStreamTeaserContainer)}
          />
        )}
        <Route path="/info-card/photos" component={PhotosContainer} />
        <Route path="/info-card/documents" component={DocumentsContainer} />
        <Route path="/info-card/press" component={PressContainer} />
        <Route path="/info-card/memorabilia" component={MemorabiliaContainer} />
        <Route path="/account" component={AccountInfo} />
        <Route path="/rustie" component={FreeTrialRust} />
        <Route path="/greenland" component={GreenlandPromo} />
        <Route path="/about-rust" component={AboutRust} />
        {isMobileView && <Route path="/about" component={withShell(ResponsiveAbout)} />}
        {isMobileView && <Route path="/credits" component={withShell(ResponsiveCredits)} />}
        {isMobileView && <Route path="/terms" component={withShell(ResponsiveTerms)} />}
        {isMobileView && <Route path="/privacy" component={withShell(ResponsivePrivacy)} />}
        {isMobileView && <Route path="/login" component={withShell(ResponsiveLogin)} />}
        {isMobileView && <Route path="/logout" component={withShell(ResponsiveLogout)} />}
        <Route path="/contact" component={isMobileView ? withShell(ResponsiveContact) : ContactContainer} />
        <Route path="/drawer" component={DrawerContainer} />
        <Route path="/friends-and-family" component={FriendsAndFamilyDrawerContainer} />
        <Route path="/outtakes" component={OuttakesDrawerContainer} />
        <Route path="/map" component={isMobileView ? withShell(ResponsiveMap) : Map} />
        <Route path="/playlists" component={PlaylistsDrawerContainer} />
        <Route path="/menu" component={GlobalMenu} />
        <Route
          path="/:youtube/info-card"
          render={(props) => <InfoCardContainer {...props} />}
        />
        <Route path="/info-card" component={isMobileView ? (props) => <ResponsiveShell><UniversalLink {...props} type="info-card" label="Go to Song" /></ResponsiveShell> : InfoCardContainer} />
        <Route path="/timeline" component={TimelineBasicContainer} />
        <Route path="/timeline-advance" component={TimelineYearsContainer} />
        <Route path="/timeline-basic" component={TimelineBasicContainer} />
        <Route path="/timeline-months" component={TimelineMonthsContainer} />
        <Route path="/album" render={(props) => isMobileView ? <ResponsiveShell><UniversalLink {...props} type="album" label="Go to Album" /></ResponsiveShell> : <AlbumModal {...props} />} />
        <Route path="/playlist" render={(props) => isMobileView ? <ResponsiveShell><UniversalLink {...props} type="playlist" label="Go to Playlist" /></ResponsiveShell> : <AlbumModal {...props} />} />
        <Route path="/film" component={AlbumModal} />
        <Route path="/request-modal" component={RequestModal} />
        <Route
          path="/performance-log/:tour"
          render={(props) => (
            <ModalWrapper {...props}>
              <ItineraryModal {...props} />
            </ModalWrapper>
          )}
        />
        <Route path="/performance-log" component={ModalWrapper} />
        <Route path="/movie-night/home" component={MovieNightHome} />
        <Route path="/movie-night/movietone" component={PPV_Page} />
        <Route
          path="/movie-night/:id?/:id?"
          render={(props) => isMobileView ? <ResponsiveShell><UniversalLink {...props} type="movie-night" label="Go to Hearse Theater" /></ResponsiveShell> : <MovieNight {...props} />}
        />
        <Route
          path="/movietone/:id?"
          render={(props) => <PPVModal {...props} />}
        />

        {/* Prefix match, deliberately: everything under /news is the framed
            paper, and the path after /news is the article slug — which can be
            multi-segment (/news/category/canada), so a :page param won't do. */}
        <Route path="/news" component={ShellNewspaperPage} />
        <Route path="/news-article" component={NewsArticle} />
        <Route path="/newsletter" component={NewsletterContainer} />
        <Route path="/getting-started" component={GettingStartedContainer} />
        <Route path="/contact" component={ContactContainer} />
        <Route path="/search" component={SearchContainer} />
        <Route path="/find-store" component={SearchRecordStoreDayContainer} />
        <Route path="/404" component={Page404} />
        <Route path="/test" component={TestContainer} />
        <Route path="/embed" component={Player} />
        <Route path="/google71d6b4927829901b.html" render={() => null} />
        {pages && mapContentfulPages(pages)}
        <Route render={() => <Page404 />} />
      </Switch>

      {background && (
        <div className="overlay">
          <Switch location={location}>
            <Route
              path="/video"
              render={(props) => (
                <HOCRequireAuth {...props}>
                  <VideoPlayerContainer {...props} />
                </HOCRequireAuth>
              )}
            />
            <Route path="/info-card/photos" component={PhotosContainer} />
            <Route path="/info-card/documents" component={DocumentsContainer} />
            <Route path="/info-card/press" component={PressContainer} />
            <Route
              path="/info-card/memorabilia"
              component={MemorabiliaContainer}
            />
            <Route path="/account" component={AccountInfo} />
            <Route path="/rustie" component={FreeTrialRust} />
            <Route path="/greenland" component={GreenlandPromo} />
            <Route path="/about-rust" component={AboutRust} />
            <Route path="/menu" component={GlobalMenu} />
            <Route
              path="/:youtube/info-card"
              render={(props) => <InfoCardContainer {...props} />}
            />
            <Route path="/info-card" component={isMobileView ? (props) => <ResponsiveShell><UniversalLink {...props} type="info-card" label="Go to Song" /></ResponsiveShell> : InfoCardContainer} />
            <Route path="/album" render={(props) => isMobileView ? <ResponsiveShell><UniversalLink {...props} type="album" label="Go to Album" /></ResponsiveShell> : <AlbumModal {...props} />} />
            <Route path="/playlist" render={(props) => isMobileView ? <ResponsiveShell><UniversalLink {...props} type="playlist" label="Go to Playlist" /></ResponsiveShell> : <AlbumModal {...props} />} />
            <Route path="/film" component={AlbumModal} />
            <Route path="/request-modal" component={RequestModal} />
            <Route path="/performance-log/:tour" component={ItineraryModal} />
            <Route path="/performance-log" component={ModalWrapper} />
            <Route path="/movie-night/home" component={MovieNightHome} />
            <Route path="/movie-night/movietone" component={PPV_Page} />
            <Route
              path="/movie-night/:id?/:id?"
              render={(props) => isMobileView ? <ResponsiveShell><UniversalLink {...props} type="movie-night" label="Go to Hearse Theater" /></ResponsiveShell> : <MovieNight {...props} />}
            />
            <Route
              path="/movietone/:id?"
              render={(props) => <PPVModal {...props} />}
            />
            <Route path="/newsletter" component={NewsletterContainer} />
            <Route
              path="/getting-started"
              component={GettingStartedContainer}
            />
            <Route path="/contact" component={isMobileView ? withShell(ResponsiveContact) : ContactContainer} />
            <Route path="/search" component={SearchContainer} />
            <Route
              path="/find-store"
              component={SearchRecordStoreDayContainer}
            />
          </Switch>
        </div>
      )}
    </MainContainer>
  );
};

const NewspaperRoutes = () => (
  <Switch>
    <Route path="/times-contrarian/top-40-tracks" component={TcEmbed} />
    <Route path="/times-contrarian/:page/article" component={TcEmbed} />
    {/* <Route path="/times-contrarian/:page" component={TcEmbed} /> */}
    <Route exact path="/times-contrarian" component={TcEmbed} />
  </Switch>
);

const AppRouter = ({ pages, location }) => {
  const currentLocation = window.location.pathname;
  if (currentLocation === "/embed") {
    return <Route path="/embed" component={Player} />;
  }
  return (
    <Switch>
      <Redirect exact from="/" to="/welcome" />
      {/* <Route path="/times-contrarian" component={NewspaperRoutes} /> */}
      <Route
        path="/"
        render={() => (
          <MainRoutes 
            pages= { pages.filter((page) => !page.hidden) }
            location = {location} />
        )}
      />
    </Switch>
  );
};

class AppRoutes extends Component {
  render() {
    const { store, metas, pages, location } = this.props;
    return (
      <Provider store={store}>
        <Fragment>
          <Helmet {...metas} />
            <AppRouter pages={pages} location = {location}  />
        </Fragment>
      </Provider>
    );
  }
}

const mapStateToProps = function (state) {
  const {
    metas,
    pages: { all },
  } = state;
  return { metas, pages: all };
};

export default withRouter(connect(mapStateToProps)(AppRoutes));
